// Read-only in-app SOURCE VIEWER: a full-page overlay showing another outlet's full
// article (archived study copy) so participants can read what that outlet published
// without leaving the study. Deliberately tool-free — no highlights, diffs, or lens
// chrome — it must not read as another lens condition. Rendered ABOVE the app (the
// underlying condition stays mounted, same philosophy as the tooltip overlays) and
// deep-linked as #/<cond>/<incidentId>/source/<outletDomain>.

import { useEffect } from "react";
import type { Bundle } from "../types";
import type { Telemetry } from "../telemetry";
import { INK, INK_DEEP, SURFACE, sectionLabel } from "../style";
import { outletDisplay } from "../labels";
import { findSource } from "./SourceLink";

export function SourceViewer({ b, outlet, tel, onClose }: {
  b: Bundle; outlet: string; tel: Telemetry; onClose: () => void;
}) {
  const src = findSource(b.sources, outlet);

  useEffect(() => {
    const t0 = Date.now();
    tel.log("source_view_start", { outlet });
    return () => tel.log("source_view_end", { outlet, dwell_ms: Date.now() - t0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet]);

  const display = src?.outlet_display || outletDisplay(outlet);

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 70, overflowY: "auto", overscrollBehavior: "contain", background: "rgba(38,36,31,.44)" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 740, margin: "0 auto", minHeight: "100vh", background: "#fff", boxShadow: "0 0 60px rgba(15,15,15,.28)", display: "flex", flexDirection: "column", fontFamily: "'Source Sans 3',sans-serif", color: INK }}>

        {/* top bar — back to the study article + secondary external original */}
        <div style={{ position: "sticky", top: 0, zIndex: 2, background: "rgba(255,255,255,.92)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(55,53,47,.1)", padding: "11px 28px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button onClick={onClose}
            style={{ font: "inherit", fontSize: 12.5, fontWeight: 600, color: INK, background: SURFACE.chip, border: "1px solid rgba(55,53,47,.14)", borderRadius: 6, padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>
            <span aria-hidden>←</span> Back to the article you were reading
          </button>
          <div style={{ flex: 1, minWidth: 8 }} />
          {src && (
            <a href={src.url} target="_blank" rel="noopener noreferrer"
              onClick={() => tel.log("source_original_open", { outlet })}
              style={{ fontSize: 12, fontWeight: 600, color: "rgba(55,53,47,.62)", textDecoration: "underline", textUnderlineOffset: "2px", textDecorationColor: "rgba(55,53,47,.3)" }}>
              Read the original on {outlet} ↗
            </a>
          )}
        </div>

        {src ? (
          <article style={{ padding: "26px 28px 64px" }}>
            {/* persistent provenance note */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: SURFACE.warmer, border: "1px solid rgba(55,53,47,.1)", borderRadius: 5, padding: "5px 10px", marginBottom: 16 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(55,53,47,.35)", flex: "none" }} />
              <span style={{ fontSize: 11, color: "rgba(55,53,47,.55)" }}>Archived study copy of this outlet's coverage, shown read-only, without lens tools.</span>
            </div>

            <div style={{ ...sectionLabel, letterSpacing: ".07em", marginBottom: 6 }}>
              {display}{src.date_published ? " · " + src.date_published : ""}
            </div>
            <h1 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 28, lineHeight: 1.22, letterSpacing: "-.015em", margin: "0 0 18px", textWrap: "balance" as any, color: INK_DEEP }}>
              {src.headline}
            </h1>
            <div style={{ fontSize: 16.5, lineHeight: 1.76 }}>
              {src.body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean).map((p, i) => (
                <p key={i} style={{ margin: "0 0 15px" }}>{p}</p>
              ))}
            </div>
          </article>
        ) : (
          <div style={{ padding: "90px 28px", textAlign: "center" }}>
            <div style={{ ...sectionLabel, letterSpacing: ".07em", marginBottom: 8 }}>{display}</div>
            <div style={{ fontSize: 14.5, color: "rgba(55,53,47,.6)", lineHeight: 1.6 }}>
              This outlet's article isn't available in the study bundle.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
